import React, { useState } from 'react';
import { tw } from 'twind';
import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';
import { Icon } from '@global-ecom/nitro-uds/elements';

import { Adjustment, Maybe, StandardOrder } from '__generated__/graphql';
import { useTranslate } from 'hooks/useTranslations';
import { useOrderDiscountValue } from 'hooks/useOrderDiscountValue';
import { useFeature } from 'hooks/useFeature';
import { isNumber } from 'utils/isNumber';
import { useDialogDismiss } from 'hooks/a11y';
import { ActiveCart } from 'types/cart';
import { useCheckoutPayment } from 'hooks/useCheckoutPayment';
import { PaymentMethod, SUPPORTED_COUNTRY } from 'utils/constants';
import { useSiteConfig } from 'hooks/useSiteConfig';
import { parseTaxTotal } from 'utils/parseTaxTotal';

import { Price } from './Price';

const KlarnaText = dynamic(() => import('ui/components/KlarnaText'), {
  ssr: false,
});

const InfoBubble = (props: { onClickOutside: () => void; text: string }) => {
  const infoRef = React.useRef(null);

  useDialogDismiss({
    ref: infoRef,
    handler: props.onClickOutside || (() => null),
  });

  return (
    <div
      ref={infoRef}
      className="w-60 p-3 border-2 right-1/4 translate-x-1/4 group absolute bottom-7 bg-white rounded cursor-default text-left"
    >
      {props.text}
      <div className="absolute w-3 h-3 -bottom-[14px] border-b-2 border-r-2 rounded-[-20px] transform right-1/4 translate-x-1/3 -translate-y-1/2 bg-white rotate-45" />
    </div>
  );
};

const Total = ({ total }: { total: number | null | undefined }) => (
  <>{total ? <Price amount={total} /> : '-'}</>
);

const TotalMinusTaxes = ({
  estimatedTotal,
  taxTotal,
}: {
  estimatedTotal: number | null | undefined;
  taxTotal: number | null | undefined;
}) => {
  const showSubtotal = isNumber(estimatedTotal);

  return (
    <>
      {showSubtotal ? (
        <Price amount={estimatedTotal - parseTaxTotal(taxTotal)} />
      ) : (
        '-'
      )}
    </>
  );
};

export const PriceBreakdown: React.FC<{
  cart?: ActiveCart;
  order?: StandardOrder;
  isEstimate?: boolean;
  /** We only show `<KlarnaText />` if this is `false`. */
  isConfirmationPage?: boolean;
}> = props => {
  const t = useTranslate();
  const entity = props.order || props.cart;

  return (
    <>
      <h3 id="price-breakdown-title" className="sr-only">
        {t('priceBreakdown')}
      </h3>
      <ul
        className="font-bold text-sm text-puma-black-300 uppercase"
        data-test-id="order-summary"
        aria-labelledby="price-breakdown-title"
      >
        <SiteWideDiscounts entity={entity} />
        <YourSavings entity={entity} />
        <Subtotal entity={entity} />
        <ShippingCosts entity={entity} />
        <ShippingSurcharge entity={entity} />
        <OrderDiscounts entity={entity} />
        <CashOnDeliveryFee entity={entity} />
        <CustomizationAdjustment entity={entity} />
        <ShippingAdjustments entity={entity} />
        <TaxTotal entity={entity} isEstimate={props.isEstimate} />
        <TotalDetails entity={entity} isEstimate={props.isEstimate} />
      </ul>

      {!props.isConfirmationPage && (
        <div className="mt-5">
          <KlarnaText amount={entity?.estimatedTotal} />
        </div>
      )}
    </>
  );
};

const SiteWideDiscounts = (props: { entity: ActiveCart | StandardOrder }) => {
  const showDiscountAmountAndTextEnabled = useFeature(
    'SHOW_DISCOUNT_AMOUNT_AND_TEXT_ENABLED'
  );
  if (!showDiscountAmountAndTextEnabled) return null;

  const siteWideDiscounts = (
    (props.entity?.orderAdjustments as Adjustment[]) ?? []
  ).filter(item => !item.couponCode);

  return (
    <>
      {siteWideDiscounts.map(({ promotionId, itemText, price }, index) => (
        <li
          key={promotionId}
          data-test-id="order-discount-line-item"
          className={tw(
            'text-puma-black',
            index === siteWideDiscounts.length - 1 && 'mb-10'
          )}
        >
          {promotionId && itemText && isNumber(price) && (
            <p className="flex justify-between whitespace-nowrap font-normal">
              <span className="whitespace-normal mr-10">{itemText}</span>
              <span className="text-puma-green">
                <Price amount={price} />
              </span>
            </p>
          )}
        </li>
      ))}
    </>
  );
};

const YourSavings = (props: { entity: ActiveCart | StandardOrder }) => {
  const t = useTranslate();
  const { staticFeatures } = useSiteConfig();

  const hideYourSavings = useFeature('HIDE_YOUR_SAVINGS');
  const showDiscountAmountAndTextEnabled = useFeature(
    'SHOW_DISCOUNT_AMOUNT_AND_TEXT_ENABLED'
  );

  const { subTotal, products } = props.entity ?? {};
  const profit = useOrderDiscountValue(subTotal, products);

  if (hideYourSavings || showDiscountAmountAndTextEnabled || !profit) {
    return null;
  }

  return (
    <li
      className={`flex justify-between ${
        staticFeatures.isPNARegion ? 'text-puma-green' : 'text-puma-gold'
      } mt-1`}
    >
      <h4>{t('yourSavings')}</h4>
      <p
        className="text-right whitespace-nowrap"
        data-test-id="savings"
        data-breakdown-row="savings"
      >
        <Price amount={profit * -1} />
      </p>
    </li>
  );
};

const Subtotal = (props: { entity: ActiveCart | StandardOrder }) => {
  const t = useTranslate();
  const { subTotal } = props.entity ?? {};

  return (
    <li className="flex justify-between mt-1">
      <h4>{t('subtotal')}</h4>
      <p
        className="text-right whitespace-nowrap"
        data-test-id="subtotal"
        data-breakdown-row="subtotal"
      >
        {subTotal ? <Price amount={subTotal} /> : '-'}
      </p>
    </li>
  );
};

const ShippingCosts = (props: { entity: ActiveCart | StandardOrder }) => {
  const t = useTranslate();
  const { estimatedShippingCosts, shippingCosts } = props.entity ?? {};

  const checkoutPayment = useCheckoutPayment();
  const {
    country,
    staticFeatures: { showFreeShippingCost },
  } = useSiteConfig();

  // We should default to displaying estimatedShippingCosts only
  // if the country is not IN and the payment method is not COD.
  // Otherwise, display shippingCost.
  const total =
    country === SUPPORTED_COUNTRY.IN &&
    checkoutPayment?.formState.currentPaymentMethod === PaymentMethod.COD
      ? shippingCosts ?? estimatedShippingCosts ?? 0
      : estimatedShippingCosts ?? shippingCosts ?? 0;
  const freeShipping = total === 0 && showFreeShippingCost;

  return (
    <li className="flex justify-between mt-1">
      <h4>{t('shippingCosts')}</h4>
      <p
        className="text-right whitespace-nowrap"
        data-test-id="shipping"
        data-breakdown-row="shipping"
      >
        {freeShipping ? <div>{t('free')}</div> : <Price amount={total} />}
      </p>
    </li>
  );
};

const ShippingSurcharge = (props: { entity: ActiveCart | StandardOrder }) => {
  const { shippingSurcharge } = props.entity ?? {};
  if (!shippingSurcharge) return null;

  return (
    <li className="flex justify-between mt-1">
      <div className="flex items-center space-x-2">
        <h4>{shippingSurcharge.label}</h4>
      </div>
      <p
        className="text-right whitespace-nowrap"
        data-test-id="shipping-surcharge"
        data-breakdown-row="shipping-surcharge"
      >
        <Price amount={shippingSurcharge.amount} />
      </p>
    </li>
  );
};

const OrderDiscounts = (props: { entity: ActiveCart | StandardOrder }) => {
  const t = useTranslate();

  const orderAdjustments = (props.entity?.orderAdjustments ??
    []) as Adjustment[];
  const orderDiscountsWithoutCodFee = orderAdjustments.filter(
    adj => adj.promotionId !== 'codFee'
  );

  if (!orderDiscountsWithoutCodFee.length) return null;

  const totalOrderDiscount = orderDiscountsWithoutCodFee.reduce((sum, item) => {
    return sum + (item.price ?? 0);
  }, 0);

  return (
    <li className="flex justify-between mt-1">
      <h4 className="uppercase">{t('orderDiscount')}</h4>
      <p
        className="text-right whitespace-nowrap"
        data-test-id="order-discount"
        data-breakdown-row="order-discount"
      >
        <Price amount={totalOrderDiscount} />
      </p>
    </li>
  );
};

const CashOnDeliveryFee = (props: { entity: ActiveCart | StandardOrder }) => {
  const t = useTranslate();

  const codFee = ((props.entity?.orderAdjustments as Adjustment[]) ?? []).find(
    adj => adj.promotionId === 'codFee'
  );

  if (!codFee || !isNumber(codFee?.price)) return null;

  return (
    <li className="flex justify-between mt-1">
      <h4 className="uppercase">{t('codFee')}</h4>
      <p
        className="text-right whitespace-nowrap"
        data-test-id="codfee-discount"
        data-breakdown-row="codfee-discount"
      >
        <Price amount={codFee.price} />
      </p>
    </li>
  );
};

const CustomizationAdjustment = (props: {
  entity: ActiveCart | StandardOrder;
}) => {
  const t = useTranslate();
  const { price } = props.entity?.customizationAdjustment ?? {};

  if (!isNumber(price)) return null;

  return (
    <li className="flex justify-between mt-1">
      <h4 className="uppercase">{t('customizationCost')}</h4>
      <p
        className="text-right whitespace-nowrap"
        data-test-id="customization-cost"
        data-breakdown-row="order-discount"
      >
        <Price amount={price} />
      </p>
    </li>
  );
};

const ShippingAdjustments = (props: { entity: ActiveCart | StandardOrder }) => {
  const checkoutPayment = useCheckoutPayment();
  const t = useTranslate();
  const { shippingAdjustments } = props.entity ?? {};

  const COD_SHIPPING_FEE = 'COD Shipping Fee';

  // Hide Shipping Discount if COD Shipping Fee applies
  if (
    !shippingAdjustments?.length ||
    (checkoutPayment?.formState.currentPaymentMethod === PaymentMethod.COD &&
      shippingAdjustments?.some(item => item.promotionId === COD_SHIPPING_FEE))
  )
    return null;

  const total = (shippingAdjustments as Adjustment[]).reduce((sum, item) => {
    const { promotionId, price } = item;
    return sum + (promotionId === COD_SHIPPING_FEE ? 0 : price ?? 0);
  }, 0);

  return (
    <li className="flex justify-between mt-1">
      <h4 className="uppercase">{t('shippingDiscount')}</h4>
      <p
        className="text-right whitespace-nowrap"
        data-test-id="shipping-discount"
        data-breakdown-row="shipping-discount"
      >
        <Price amount={total} />
      </p>
    </li>
  );
};

const TaxTotal = (props: {
  entity: ActiveCart | StandardOrder;
  isEstimate?: boolean;
}) => {
  const t = useTranslate();
  const router = useRouter();
  const { taxation, taxTotal, shipment } = props.entity ?? {};

  const isCartPage = router.pathname.includes('/cart');

  const showTaxTotal = shouldTaxTotalRender({
    taxTotal,
    isCartPage,
    postalCode: shipment?.address?.postalCode,
  });

  const [showSalesTaxInfo, setShowSalesTaxInfo] = useState(false);

  if (taxation === 'gross') return null;

  return (
    <li className="flex justify-between mt-1">
      <div className="flex">
        <h4>{t(props.isEstimate ? 'estimatedSalesTax' : 'salesTax')}</h4>
        <button
          className="ml-2 relative"
          type="button"
          onClick={() => setShowSalesTaxInfo(true)}
        >
          <span className="sr-only">{t('learnTax')}</span>
          {isCartPage && <Icon name="info" size="xl" />}
          {showSalesTaxInfo && (
            <InfoBubble
              onClickOutside={() => setShowSalesTaxInfo(false)}
              text={t('calculatedTaxToolTip')}
            />
          )}
        </button>
      </div>
      <p
        className="text-right whitespace-nowrap"
        data-test-id="sales-tax"
        data-breakdown-row="sales-tax"
      >
        {showTaxTotal ? <Price amount={taxTotal as number} /> : <span>_</span>}
      </p>
    </li>
  );
};

const TotalDetails = (props: {
  entity: ActiveCart | StandardOrder;
  isEstimate?: boolean;
}) => {
  const t = useTranslate();
  const router = useRouter();
  const { taxation, taxTotal, shipment, estimatedTotal } = props.entity ?? {};
  const isCartPage = router.pathname.includes('/cart');

  const showTaxTotal = shouldTaxTotalRender({
    taxation,
    taxTotal,
    isCartPage,
    postalCode: shipment?.address?.postalCode,
  });

  return (
    <li className="flex justify-between pt-6 border-t border-puma-black-500 mt-6 font-bold text-xl text-puma-black">
      <div
        className="flex flex-wrap items-baseline"
        data-test-id={
          props.isEstimate ? 'estimated-total-title' : 'total-title'
        }
      >
        <h4 className="mr-2.5">
          {t(props.isEstimate ? 'estimatedTotal' : 'total')}
          {t('priceClarification') && (
            <span className="block text-sm text-puma-black-300 xs:inline xs:pl-2">
              {t('priceClarification')}
            </span>
          )}
        </h4>
      </div>

      <p className="whitespace-nowrap" data-test-id="estimated-total">
        {showTaxTotal ? (
          <Total total={estimatedTotal} />
        ) : (
          <TotalMinusTaxes
            estimatedTotal={estimatedTotal}
            taxTotal={taxTotal}
          />
        )}
      </p>
    </li>
  );
};

const shouldTaxTotalRender = (args: {
  taxation?: Maybe<'gross' | 'net'>;
  taxTotal?: Maybe<number>;
  isCartPage: boolean;
  postalCode?: string;
}) => {
  if (args.taxation === 'gross') return true; // Always show if taxation is gross
  if (!isNumber(args.taxTotal)) return false; // Don't show if we don't have a valid tax total
  if (args.isCartPage) return false; // Don't show on cart page
  if (!args.postalCode) return false; // Don't show if we don't have a postal code

  return true;
};
